import { useQuery } from '@tanstack/react-query';
import Card from '../../components/common/Card';
import { getClasses } from '../../services/classService';
import { MdSchedule } from 'react-icons/md';
import { CLASS_TYPE_CARD } from '../../utils/classType';

const ALL_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const todayIdx = new Date().getDay();

const timeStr = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hr = parseInt(h);
  return `${hr % 12 || 12}:${m} ${hr >= 12 ? 'PM' : 'AM'}`;
};

const TYPE_STYLE = CLASS_TYPE_CARD;

function MySchedule() {
  document.title = 'My Schedule — TrainAxis';
  const { data, isPending } = useQuery({
    queryKey: ['classes'],
    queryFn:  () => getClasses({ isActive: 'true' }).then(r => r.data),
  });

  const classes = data?.classes || [];

  const byDay = ALL_DAYS.reduce((acc, d) => {
    acc[d] = classes.filter(c => c.days?.includes(d)).sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
    return acc;
  }, {});

  return (
    <div>
      <p className="text-gray-400 text-sm mb-6">{classes.length} active class{classes.length !== 1 ? 'es' : ''} in schedule</p>

      {isPending ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          {Array.from({ length: 7 }).map((_, i) => (
            <div key={i} className="h-32 bg-gray-100 animate-pulse rounded-2xl" />
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <div className="grid grid-cols-7 gap-3 min-w-[700px]">
            {ALL_DAYS.map((day, idx) => {
              const isToday = idx === todayIdx;
              return (
                <div key={day}>
                  {/* Day header */}
                  <div className={`rounded-xl px-2 py-2 text-center mb-2 ${isToday ? 'bg-primary text-white' : 'bg-gray-50'}`}>
                    <p className={`text-xs font-bold ${isToday ? 'text-white' : 'text-secondary'}`}>
                      {day.slice(0, 3).toUpperCase()}
                    </p>
                    {isToday && <p className="text-[10px] text-white/70 mt-0.5">Today</p>}
                  </div>

                  {/* Class cards */}
                  {byDay[day].length ? byDay[day].map(c => (
                    <div
                      key={c._id}
                      className={`rounded-xl border p-2.5 text-xs mb-2 transition-colors duration-150 hover:border-secondary ${TYPE_STYLE[c.type] || 'bg-gray-50 border-gray-200'}`}
                    >
                      <p className="font-semibold truncate leading-snug">{c.name}</p>
                      {c.startTime && (
                        <p className="mt-1 text-[11px] font-medium opacity-80">{timeStr(c.startTime)}</p>
                      )}
                      <p className="mt-0.5 text-[10px] opacity-60 capitalize">{c.type}</p>
                    </div>
                  )) : (
                    <div className="rounded-xl border border-dashed border-gray-200 p-3 text-center">
                      <p className="text-[10px] text-gray-400">Rest</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-2 mt-4 mb-2">
        {Object.entries(TYPE_STYLE).map(([type, cls]) => (
          <span key={type} className={`text-[10px] font-semibold px-2.5 py-1 rounded-full border ${cls}`}>
            {type}
          </span>
        ))}
      </div>

      {/* Upcoming this week */}
      <Card className="mt-4">
        <h2 className="font-semibold text-secondary text-sm mb-3 flex items-center gap-2">
          <MdSchedule className="text-primary" /> Upcoming Classes This Week
        </h2>
        {ALL_DAYS.slice(todayIdx, todayIdx + 4).flatMap(day =>
          (byDay[day] || []).map(c => (
            <div key={`${day}-${c._id}`} className="flex items-center gap-3 py-2.5 border-b border-gray-100 last:border-0 hover:bg-nav-hover rounded-lg px-1 transition-colors">
              <div className="w-10 text-center flex-shrink-0">
                <p className="text-xs font-bold text-primary">{day.slice(0, 3)}</p>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-secondary truncate">{c.name}</p>
                <p className="text-xs text-gray-400">{c.discipline} · {c.room || 'Main Hall'}</p>
              </div>
              <p className="text-sm font-semibold text-gray-600 flex-shrink-0">{timeStr(c.startTime)}</p>
            </div>
          ))
        )}
        {ALL_DAYS.slice(todayIdx, todayIdx + 4).flatMap(day => byDay[day] || []).length === 0 && (
          <p className="text-sm text-muted py-4 text-center">No classes scheduled for the next 4 days</p>
        )}
      </Card>
    </div>
  );
}

export default MySchedule;
